import React, { useState, useEffect, useRef } from 'react';
import { Search } from './search';
import styles from './header.module.scss';
import { Switch, Tooltip, notification } from 'antd';
import { ROUTES } from 'shared/constants/routes';
import { Lang, DATA_KEYS, colorPalette, EVENTS } from 'shared/constants';
import { MobileQRCode } from '../../../mobile-qr-code';
import { Link, NavLink } from 'react-router-dom';
import { GiveFeedbackReport } from 'src/components/issue-report/give-feedback-report';
import { useNavigate, useLocation } from 'react-router-dom';
import { AskPandaButton } from 'src/components/ai';
import { SwitchOrganizationDropdown } from 'src/pages/application/switchOrganization/switch-organization-dropdown';
import { sortByAlphabet } from 'shared/utils';
import { Dropdown } from 'src/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/pro-thin-svg-icons';
import { UserAvatar } from 'src/components/avatar';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import { resetSearchTracking } from 'src/pages/application/search/Search.page.component';
import analyticsService from 'src/helpers/analytics.service';

const { HOME_PAGE } = DATA_KEYS;

const HeaderComponent = React.memo(function HeaderComponent({
  lang = Lang.HEADER,
  logout = () => {},
  adminMode = false,
  setAdminMode = () => {},
  isCollaborator = false,
  isAccountAdmin = false,
  workspaces,
  selectedWorkspaceId,
  selectWorkspace,
  forceNewSearch = () => {},
  isAIEnabled,
  isCanEditProfile,
  canAccessSettings,
  user = {},
  organization = {},
  isSwitchOrganizationAvailable = false,
  feedbackForm = 'mailto:contact@c8health.com',
}) {
  const [showIssueReportPopup, setShowIssueReportPopup] = useState(false);
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [notificationApi, contextHolder] = notification.useNotification();
  const navigate = useNavigate();
  const mobileQRCodeRef = useRef(null);
  const location = useLocation();

  // Track screen size to show/hide profile menu item
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 900px)');
    setIsSmallScreen(mediaQuery.matches);

    const handleChange = e => setIsSmallScreen(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const openIssueReportPopup = () => {
    setShowIssueReportPopup(true);
  };

  const closeIssueReportPopup = () => {
    setShowIssueReportPopup(false);
  };

  const handleSettingsDropdownOpenChange = open => {
    setSettingsDropdownOpen(open);
  };

  const onSelectWorkspace = key => {
    searchContextIdManager.resetContext();
    resetSearchTracking();

    analyticsService.track(EVENTS.WORKSPACE.SWITCH, {
      sourceWorkspaceId: selectedWorkspaceId,
      targetWorkspaceId: key,
    });

    const selectedName = workspaces.find(ws => ws.id === key)?.name;
    const showSwitchToast = () => {
      if (selectedName) {
        notificationApi.success({
          message: lang.CHANGE_WORKSPACE_SUCCESS.replace('{name}', selectedName),
          placement: 'bottomLeft',
          duration: 10,
        });
      }
    };

    if (location.pathname === ROUTES.KNOWLEDGE_HUB) {
      selectWorkspace(key);
      showSwitchToast();
      return;
    }
    // If on search page, re-trigger search for new workspace results
    if (location.pathname === ROUTES.SEARCH) {
      selectWorkspace(key);
      showSwitchToast();
      setTimeout(() => forceNewSearch(), 0);
      return;
    }

    selectWorkspace(key);
    showSwitchToast();
    navigate(
      {
        pathname: `/${location.pathname.split('/')[1]}`,
        search: '',
      },
      { replace: true },
    );
  };

  const renderWorkspaceSelector = () => {
    if (!workspaces?.length || workspaces?.length === 1) return;

    const currentWorkspace = workspaces.find(ws => ws.id === selectedWorkspaceId);
    return (
      <Dropdown
        bordered
        items={[...workspaces]
          .sort((a, b) => sortByAlphabet(a.name, b.name))
          .map(workspace => ({
            key: workspace?.id,
            label: workspace?.name,
          }))}
        onSelect={onSelectWorkspace}
        activeKey={selectedWorkspaceId}
        showArrowIcon
        childrenWrapperStyle={styles.workspaceDropdownWrapper}
        aria-label="workspace selector">
        <p className={styles.selectedWorkspaceName}>{currentWorkspace?.name}</p>
      </Dropdown>
    );
  };

  return (
    <>
      {contextHolder}
      <GiveFeedbackReport
        open={showIssueReportPopup}
        close={closeIssueReportPopup}
        notificationApi={notificationApi}
      />
      <header className={styles.root}>
        <div className={styles.dropdowns}>
          <SwitchOrganizationDropdown successMessage={lang.SUCCESSFUL_SWITCH_ORG} />

          {renderWorkspaceSelector()}
        </div>

        <div className={styles.searchWrapper}>
          <Search />
        </div>
        <div className={styles.rightSideWrapper}>
          {isAIEnabled && <AskPandaButton />}

          {isCanEditProfile && (
            <Tooltip
              title={lang.PROFILE}
              placement="left"
              trigger={['focus', 'hover']}
              className={styles.avatarTooltip}>
              <Link
                to={ROUTES.USER_PROFILE}
                className={styles.profileButton}
                onClick={() => searchContextIdManager.markSearchInactive()}>
                <UserAvatar
                  className={styles.profileAvatar}
                  user={user}
                  size="small"
                  color={colorPalette.primaryMain9}
                />
              </Link>
            </Tooltip>
          )}

          <Dropdown
            items={[
              {
                key: HOME_PAGE.PROFILE,
                label: <NavLink to={ROUTES.USER_PROFILE}>{lang.PROFILE}</NavLink>,
                hide: !isCanEditProfile || !isSmallScreen,
                onClick: () => searchContextIdManager.markSearchInactive(),
              },
              {
                key: HOME_PAGE.SETTINGS,
                label: <NavLink to={ROUTES.SETTINGS}>{lang.SETTINGS}</NavLink>,
                hide: !canAccessSettings,
                onClick: () => searchContextIdManager.markSearchInactive(),
              },
              {
                key: HOME_PAGE.USERS_MANAGEMENT,
                label: <NavLink to={ROUTES.USERS_MANAGEMENT}>{lang.USERS_MANAGEMENT}</NavLink>,
                hide: !isAccountAdmin,
                onClick: () => searchContextIdManager.markSearchInactive(),
              },
              {
                key: HOME_PAGE.ADMIN_MODE,
                label: (
                  <div className={styles.adminMode}>
                    {lang.ADMIN_MODE_TOGGLER}
                    <Switch
                      size="small"
                      checked={adminMode}
                      onChange={setAdminMode}
                      className={styles.toggleAdmin}
                    />
                  </div>
                ),
                hide: !isCollaborator,
                onClick: e => {
                  e.domEvent.stopPropagation();
                  e.domEvent.preventDefault();
                  setAdminMode(!adminMode);
                },
              },
              {
                key: HOME_PAGE.GIVE_FEEDBACK,
                label: lang.GIVE_FEEDBACK,
                onClick: openIssueReportPopup,
              },
              {
                key: HOME_PAGE.TUTORIALS,
                label: (
                  <a href="https://help.c8health.com/" target="_blank" rel="noreferrer">
                    {lang.TUTORIALS}
                  </a>
                ),
              },
              {
                key: HOME_PAGE.LOGOUT,
                label: lang.LOGOUT,
                onClick: logout,
              },
              {
                key: HOME_PAGE.MOBILE_QR_CODE,
                label: <MobileQRCode ref={mobileQRCodeRef} />,
                onClick: e => {
                  e.domEvent.stopPropagation();
                  e.domEvent.preventDefault();
                  mobileQRCodeRef.current?.openDialog();
                },
                style: {
                  padding: 0,
                  margin: 0,
                  width: '100%',
                },
              },
            ]}
            open={settingsDropdownOpen}
            onOpenChange={handleSettingsDropdownOpenChange}
            menu={{ style: { margin: '0 0.25rem' } }}
            childrenWrapperStyle={styles.settingsWrapper}
            aria-label={lang.SETTINGS}>
            <FontAwesomeIcon icon={faGear} size="2x" color={colorPalette.neutral1} />
          </Dropdown>
        </div>
      </header>
    </>
  );
});

export { HeaderComponent };
